import { Controller } from 'stimulus';
import addressResolver from '@/helpers/addressResolver';
import debounce from '@/helpers/debounce';

/**
 *  Autofill address based on postal code and house number
 *
 * @class AddressAutofill
 * @extends {Controller}
 */
class AddressAutofill extends Controller {
  connect() {
    this.POSTAL_CODE_LENGTH = 6;
    this.HOUSE_NUMBER_LENGTH = 1;

    this.debouncedCheck = debounce(this.runCheck.bind(this), 20);

    if (this.postalCodeTarget.value !== '' && this.houseNumberTarget.value !== '') {
      this.showChangeFieldsButton(true);
    }
  }

  /**
   * Small wrapper around the API call, this will handle errors and loading states
   *
   * @returns {Void}
   * @memberof AddressAutofill
   */
  runCheck() {
    const postalCode = this.postalCodeTarget.value.trim().replace(' ', '');
    const houseNumber = this.houseNumberTarget.value.trim().replace(' ', '');
    const country = 'nl';

    if (postalCode.length !== this.POSTAL_CODE_LENGTH
      || houseNumber.length < this.HOUSE_NUMBER_LENGTH) {
      return;
    }

    this.disableStreetCityFields(true);
    this.showChangeFieldsButton(false);
    this.toggleLoadingSpinner(true);

    addressResolver(postalCode, houseNumber, country)
      .then((data) => {
        this.outputData(data);
        this.showChangeFieldsButton(true);
        this.toggleLoadingSpinner(false);
      })
      .catch(() => {
        this.disableStreetCityFields(false);
        this.toggleLoadingSpinner(false);
      });
  }

  /**
   * Output data to the DOM, but only if the input fields are still empty
   *
   * @param  {Object} data API object with all address information
   * @return {Void}
   * @memberof AddressAutofill
   */
  outputData(data) {
    if (data.length === 0) {
      this.streetTarget.value = '';
      this.cityTarget.value = '';

      this.errorsTarget.innerText = window.app.i18n.addressNotFound;
      this.disableStreetCityFields(false);

      return;
    }

    this.streetTarget.value = data.street;
    this.cityTarget.value = data.city;

    this.disableStreetCityFields(true);
    this.showChangeFieldsButton(true);
  }

  /**
   * Toggle preview fields
   *
   * @param state Boolean
   * @memberof AddressAutofill
   */
  showChangeFieldsButton(state) {
    this.changeFieldsButtonTarget.style.display = state ? 'flex' : 'none';
  }

  /**
   * Disable street and city fields
   *
   * @param state Boolean
   * @memberof AddressAutofill
   */
  disableStreetCityFields(state) {
    this.cityTarget.toggleAttribute('readonly', state);
    this.streetTarget.toggleAttribute('readonly', state);
  }

  /**
   * Toggle loading state for street and city fields
   *
   * @param state Boolean
   * @memberof AddressAutofill
   */
  toggleLoadingSpinner(state) {
    this.cityTarget.parentElement.classList.toggle('is-loading', state);
    this.streetTarget.parentElement.classList.toggle('is-loading', state);
  }

  onBlur() {
    this.debouncedCheck();
  }

  onCountryChange(event) {
    const { currentTarget } = event;

    this.postalCodeTarget.value = '';
    this.houseNumberTarget.value = '';
    this.streetTarget.value = '';
    this.cityTarget.value = '';

    this.disableStreetCityFields(parseInt(currentTarget.value, 10) === 1);
    this.showChangeFieldsButton(false);
  }

  onChangeFieldsButtonClick() {
    this.disableStreetCityFields(false);
    this.showChangeFieldsButton(false);
  }
}

AddressAutofill.targets = [
  'postalCode',
  'houseNumber',
  'street',
  'city',
  'errors',
  'changeFieldsButton',
  'streetCityFields',
];

export default AddressAutofill;
